import * as joda from "@js-joda/core";
import h from "trader-hyperscript";
import { rpc } from "trader.ts/network";
import { Source } from "trader.ts/types/source";
import { dyn, dynVariadic, scheduleForCleanup } from "trader.ts/ui";
import { Form, constantField, selectBox, timeBox } from "trader.ts/ui.forms";
import { checkAllCasesHandled } from "trader.ts/utils";
import {
  enrollmentid,
  formulaid,
  internalteacherid,
  lessongroupid,
  planningmode,
  studentid,
} from "../../../sql/classy/types";
import * as FA from "../../../styles/fontawesome.css.classes";
import * as planningstyle from "../../../styles/planning.css.classes";
import * as style from "../../../styles/style.css.classes";
import {
  radioField,
  radioFieldCheckboxRenderInputLike,
} from "../../client/radiofield";
import { renderAsync } from "../../client/renderasync";
import { renderSaveButton } from "../../client/savebutton";
import { country, formulaFull } from "../../domain/config";
import { adaptInvoiceLineToLg } from "../../domain/finance";
import { period } from "../../domain/periods";
import {
  formulaColorToPlanningColors,
  greyPlanningColors,
  makeGradient,
  paleBlue,
} from "../../lib/colors";
import { isEqual } from "../../lib/isequal";
import { isNil, isNumber } from "../../lib/istype";
import { getFirstWeekdayFrom, getMonday } from "../../lib/time";
import {
  parseEnrollmentRequestStatus,
  parseFormulaType,
  parseWeekday,
  singleDueDateCalculation,
} from "../../parsing_from_ur";
import {
  HHmm,
  ddMM,
  ddMMMMyyyy,
  ddMMyyyy,
  renderEnrollmentStatus,
  renderEnrollmentrequestStatus,
  renderFirstNameLastName,
  renderLessonLocation,
  renderTimeRange,
  wrapInParentheses,
  wrapInParenthesesIfNotEmpty,
} from "../../render";
import * as routes from "../../routes";
import { translateWeekday } from "../../translations_specific";
import { schoolurl } from "../../types";
import {
  planningClientMode,
  planningClientOptions,
  planning_slot,
  planning_slot_contents_enrollmentrequest,
  planning_slot_contents_lesson,
  planning_slot_teacher_absence,
  planning_slot_teaching_period,
} from "./client";
import { mkEnrollmentFormOnPlanning } from "./enrollmentform";
import { isSameSlot } from "./isSameSlot";
import {
  editLessongroupFormOptions,
  lessongroupFormArgs,
  makeMoveForm,
  mkLessongroupForm,
  renderEditLessonForm,
} from "./lessongroupform";
import {
  makeStopForm,
  renderDeleteLessongroupPanel,
} from "./stopanddeleteforms";
import { renderTeacherAbsenceForm } from "./teacherabsenceform";
import { isStudentActiveAtLesson } from "../../lib/isstudentactiveatlesson";
import { match } from "ts-pattern";

export type PlanningSelectedSlotDetails =
  | {
      tag: "selected_existing_slot";
      mode: Extract<planningClientMode, { tag: "selected_existing_slot" }>;
      slot: planning_slot;
    }
  | {
      tag: "making_complement";
      mode: Extract<planningClientMode, { tag: "making_complement" }>;
      slot: planning_slot;
    }
  | {
      tag: "moving_enrollment_selected";
      mode: Extract<planningClientMode, { tag: "moving_enrollment_selected" }>;
      slot: planning_slot;
    }
  | {
      tag: "editing";
      mode: Extract<planningClientMode, { tag: "editing" }>;
      slot: planning_slot;
    }
  | {
      tag: "moving_selected";
      mode: Extract<planningClientMode, { tag: "moving_selected" }>;
    }
  | {
      tag: "selected_new";
      mode: Extract<planningClientMode, { tag: "selected_new" }>;
    };

export function renderSelectionAndForms(
  opts: planningClientOptions,
  slots: planning_slot[],
  sources: { timeS: Source<joda.LocalTime>; durationS: Source<number> },
  reload: (
    sl: {
      weekday: joda.DayOfWeek;
      time: joda.LocalTime;
      internalteacherid: internalteacherid;
    } | null,
  ) => void,
  setMode: (m: planningClientMode) => void,
  details: PlanningSelectedSlotDetails,
): HTMLElement | HTMLElement[] {
  const goToNormal = () => setMode({ tag: "normal" });
  const contents = (function () {
    if (details.tag === "selected_existing_slot") {
      const mode = details.mode;
      const date = opts.days.find((d) =>
        isEqual(d.date.dayOfWeek(), mode.weekday),
      )!;
      const selectedSlot = details.slot;
      if (!selectedSlot) {
        return <div></div>;
      }

      if (selectedSlot.contents.tag === "lesson") {
        const lessongroupid = selectedSlot.contents.lessongroup.id;
        return renderExistingSlot_lesson(
          date.date,
          selectedSlot,
          selectedSlot.contents,
          opts.country,
          opts.schoolurl,
          opts.allFormulas,
          opts.period,
          opts.each_period_fee_due_date,
          opts.each_period_fee_pay_during_online_enrollment,
          (sl) => setMode({ tag: "editing", ...sl }),
          () =>
            setMode({
              tag: "making_complement",
              ...selectedSlot,
              lessongroupid,
            }),
          () => reload(selectedSlot),
          goToNormal,
        );
      } else if (selectedSlot.contents.tag === "enrollmentrequest") {
        return renderExistingSlot_enrollmentrequest(
          date.date,
          selectedSlot,
          selectedSlot.contents,
          goToNormal,
        );
      } else if (selectedSlot.contents.tag === "teacher_absence") {
        return renderExistingSlot_teacherabsence(
          date.date,
          selectedSlot,
          selectedSlot.contents,
          () => reload(null),
          goToNormal,
        );
      } else if (selectedSlot.contents.tag === "teaching_period") {
        return renderExistingSlot_teachingperiod(
          date.date,
          selectedSlot,
          selectedSlot.contents,
          () => reload(null),
          goToNormal,
        );
      } else {
        return checkAllCasesHandled(selectedSlot.contents);
      }
    } else if (details.tag === "selected_new") {
      const mode = details.mode;
      const firstLessonDate = opts.days.find((d) =>
        isEqual(d.date.dayOfWeek(), mode.weekday),
      )!;
      const lessongroupFormArgs: lessongroupFormArgs = {
        today: joda.LocalDate.now(),
        country: opts.country,
        schoolurl: opts.schoolurl,
        firstLessonDate: firstLessonDate.date,
        weekday: mode.weekday,
        teacher: opts.allInternalTeachers.find(
          (t) => t.internalteacherid === mode.internalteacherid,
        )!,
        limitBy: { tag: "slots", slots },
        sources: sources,
        instruments: opts.allInstruments,
        formulas: opts.allFormulas,
        period: opts.period,
        nextPeriod: opts.nextPeriod,
        onSuccessfulSave: reload,
        each_period_fee_due_date: opts.each_period_fee_due_date,
        each_period_fee_pay_during_online:
          opts.each_period_fee_pay_during_online_enrollment,
      };
      if (mode.subtype === "private") {
        return (
          <div>
            <div className={style.panel_header + planningstyle.slot_new}>
              {renderClosePopupButton(goToNormal)}
              {"Nieuwe privéles"}
            </div>
            {renderNew_private(lessongroupFormArgs, goToNormal)}
          </div>
        );
      } else if (mode.subtype === "group") {
        return (
          <div>
            <div className={style.panel_header + planningstyle.slot_new}>
              {renderClosePopupButton(goToNormal)}
              {"Nieuwe groepsles"}
            </div>
            {renderNew_group_or_camp(lessongroupFormArgs, goToNormal, "Group")}
          </div>
        );
      } else if (mode.subtype === "camp") {
        return (
          <div>
            <div className={style.panel_header + planningstyle.slot_new}>
              {renderClosePopupButton(goToNormal)}
              {"Nieuw kamp"}
            </div>
            {renderNew_group_or_camp(lessongroupFormArgs, goToNormal, "Camp")}
          </div>
        );
      } else if (mode.subtype === "free") {
        return renderNew_teachingperiod(
          mode.weekday,
          mode.internalteacherid,
          sources,
          reload,
          goToNormal,
          firstLessonDate.date,
        );
      } else if (mode.subtype === "absence") {
        return renderNew_absence(
          firstLessonDate.date,
          mode.internalteacherid,
          sources,
          slots,
          reload,
          goToNormal,
        );
      } else {
        return checkAllCasesHandled(mode.subtype);
      }
    } else if (details.tag === "making_complement") {
      const mode = details.mode;
      const firstLessonDate = opts.days.find((d) =>
        isEqual(d.date.dayOfWeek(), mode.weekday),
      )!;
      const groupOrPrivateS: Source<"group" | "private"> = new Source(
        "private",
      );

      return renderAsync(
        rpc(routes.getActiveLessonsForLessongroupSpec, undefined, {
          lessongroupid: mode.lessongroupid,
        }),
        (lessonsOfComplement) => (
          <div>
            {dynVariadic([groupOrPrivateS], ([groupOrPrivate]) => {
              const selectedSlot = slots.find((s) => isSameSlot(s, mode));
              if (!selectedSlot || selectedSlot.contents.tag !== "lesson") {
                return <div></div>;
              }
              const formulaColors = formulaColorToPlanningColors(
                selectedSlot.contents.formula.color,
              );

              const gradient = makeGradient([formulaColors, paleBlue]);

              const header = (
                <div>
                  <div
                    className={style.panel_header}
                    style={`background: ${gradient}; color: ${formulaColors.text}`}
                  >
                    {renderClosePopupButton(goToNormal)}
                    {"Nieuwe les op andere weken"}
                  </div>
                  <div>
                    <div
                      className={style.equal_columns_non_responsive}
                      style="margin: -8px; margin-bottom: 16px;"
                    >
                      <button
                        className={
                          groupOrPrivate === "private"
                            ? style.button_em_down + planningstyle.slot_new
                            : style.button_em_down
                        }
                        style="margin-right: 8px"
                        onclick={() => groupOrPrivateS.set("private")}
                      >
                        {"Privéles"}
                      </button>
                      <button
                        className={
                          groupOrPrivate === "group"
                            ? style.button_em_down + planningstyle.slot_new
                            : style.button_em_down
                        }
                        checked={groupOrPrivate === "group"}
                        onclick={() => groupOrPrivateS.set("group")}
                      >
                        {"Groepsles"}
                      </button>
                    </div>
                  </div>
                </div>
              );
              sources.timeS.set(selectedSlot.time);
              const complementLessongroupFormArgs: lessongroupFormArgs = {
                today: joda.LocalDate.now(),
                country: opts.country,
                schoolurl: opts.schoolurl,
                firstLessonDate: firstLessonDate.date,
                weekday: mode.weekday,
                teacher: opts.allInternalTeachers.find(
                  (t) => t.internalteacherid === mode.internalteacherid,
                )!,
                limitBy: {
                  tag: "complement",
                  duration: mode.duration,
                  lessonsOfComplement: lessonsOfComplement.map((l) => l.date),
                },
                sources: sources,
                instruments: opts.allInstruments,
                formulas: opts.allFormulas,
                period: opts.period,
                nextPeriod: opts.nextPeriod,
                onSuccessfulSave: reload,
                each_period_fee_due_date: opts.each_period_fee_due_date,
                each_period_fee_pay_during_online:
                  opts.each_period_fee_pay_during_online_enrollment,
              };
              if (groupOrPrivate === "group") {
                return (
                  <div>
                    {header}
                    {renderNew_group_or_camp(
                      complementLessongroupFormArgs,
                      goToNormal,
                      "Group",
                    )}
                  </div>
                );
              } else {
                return (
                  <div>
                    {header}
                    {renderNew_private(
                      complementLessongroupFormArgs,
                      goToNormal,
                    )}
                  </div>
                );
              }
            })}
          </div>
        ),
      );
    } else if (details.tag === "editing") {
      const mode = details.mode;
      const datefrom = opts.days.find((d) =>
        isEqual(d.date.dayOfWeek(), mode.weekday),
      )!;
      const selectedSlot = details.slot;
      if (!selectedSlot || selectedSlot.contents.tag !== "lesson") {
        return <div></div>;
      } else {
        const contents = selectedSlot.contents;
        return renderLessonEdit(
          selectedSlot.contents,
          {
            today: joda.LocalDate.now(),
            teacher: opts.allInternalTeachers.find(
              (t) => t.internalteacherid === mode.internalteacherid,
            )!,
            instruments: opts.allInstruments,
            formulas: opts.allFormulas,
            period: opts.period,
            reload: () => reload(selectedSlot),
            handleDeleteDone: () => reload(null),
            slots: slots,
            sources: sources,
            datefrom: datefrom.date,
            country: opts.country,
            teacherMode: opts.teacherMode,
          },
          {
            onStartMoving: (mode) =>
              setMode({
                tag: "moving",
                mode: mode,
                moving: {
                  lessongroupid: contents.lessongroup.id,
                  lessonid: contents.lesson.id,
                  formulaColor: contents.formula.color,
                  ...selectedSlot,
                },
              }),
            onStartMovingEnrollment: (student) =>
              setMode({
                tag: "moving_enrollment",
                moving: {
                  lessongroupid: contents.lessongroup.id,
                  student,
                  weekday: selectedSlot.weekday,
                  time: selectedSlot.time,
                  internalteacherid: selectedSlot.internalteacherid,
                  duration: selectedSlot.duration,
                },
              }),
          },
          goToNormal,
        );
      }
    } else if (details.tag === "moving_selected") {
      const mode = details.mode;
      const datefrom = opts.days.find((d) =>
        isEqual(d.date.dayOfWeek(), mode.moving.weekday),
      )!;
      const movingSlot = slots.find((s) => isSameSlot(s, mode.moving));
      if (!movingSlot || movingSlot.contents.tag !== "lesson") {
        return <div></div>;
      } else {
        return renderMovePopupContents(
          mode.mode,
          movingSlot.contents,
          {
            teacher: opts.allInternalTeachers.find(
              (it) => it.internalteacherid === mode.selected.internalteacherid,
            )!,
            weekday: mode.selected.weekday,
            initialTime: mode.selected.initial_time,
          },
          {
            datefrom: datefrom.date,
            reload: reload,
            slots: slots,
            sources: sources,
          },
        );
      }
    } else if (details.tag === "moving_enrollment_selected") {
      const mode = details.mode;
      const movingSlot = slots.find((s) => isSameSlot(s, mode.moving));
      const movingToSlot = slots.find((s) => isSameSlot(s, mode.selected));
      if (
        !movingSlot ||
        movingSlot.contents.tag !== "lesson" ||
        !movingToSlot ||
        movingToSlot.contents.tag !== "lesson"
      ) {
        return <div></div>;
      } else {
        return renderMovingEnrollment(
          movingSlot.contents,
          mode.moving.student,
          movingToSlot.contents,
          reload,
        );
      }
    } else {
      return checkAllCasesHandled(details);
    }
  })();
  return <div className={style.panel + style.panel_as_popup}>{contents}</div>;
}

function renderExistingSlot_lesson(
  date: joda.LocalDate,
  selectedSlot: planning_slot,
  l: planning_slot_contents_lesson,
  country: country,
  schoolurl: schoolurl,
  allFormulas: formulaFull[],
  period: period,
  each_period_fee_due_date: singleDueDateCalculation,
  each_period_fee_pay_during_online: boolean,
  onStartEdit: (sl: {
    weekday: joda.DayOfWeek;
    time: joda.LocalTime;
    internalteacherid: internalteacherid;
  }) => void,
  onStartMakingComplement: () => void,
  reload: () => void,
  goToNormal: () => void,
) {
  const addingStudentS = new Source(false);
  const formulaType = parseFormulaType(l.lessongroup.type);

  const header = renderExistingLessonHeader(l, goToNormal);

  const instrumentAndLocationSection = (
    <article style="display: flex">
      <div style="flex: 1" className={style.text_with_icon}>
        <i className={FA.icon_music_solid}></i>
        <div>{l.instrument.description}</div>
      </div>
      <div style="text-align: right" className={style.text_with_icon}>
        <div>
          {renderLessonLocation(
            l.lessongroup.location,
            l.room?.description || null,
          )}
        </div>
        <i className={FA.icon_map_marker_alt_solid + style.icon_on_right}></i>
      </div>
    </article>
  );

  const lessongroupCommentsSection =
    l.lessongroup.description === null ? (
      <span></span>
    ) : (
      <article className={style.text_with_icon}>
        <i className={FA.icon_info_solid}></i>
        <div>{[l.lessongroup.description]}</div>
      </article>
    );

  const lastLessonSection = l.lessongroup.lastLesson?.isEqual(date) ? (
    <article>
      <i>*</i> {"Laatste les"}
      {maybeRenderFlexCount(l.lessongroup, l.formula)}
    </article>
  ) : (
    <span></span>
  );

  const studentsSection = l.students
    .filter((s) => isStudentActiveAtLesson(l.lesson.date, s))
    .orderBy(
      [(s) => s.enrollment_status, renderFirstNameLastName],
      ["asc", "asc"],
    )
    .map((s) => {
      const status = s.enrollment_status;
      return (
        <div className={style.text_with_icon}>
          <i className={FA.icon_user_solid}></i>
          <div>
            <a
              href={routes.studentOverviewRoute.link({
                studentid: s.id,
              })}
            >
              {renderFirstNameLastName(s)}{" "}
              {s.enrollment_paid === false ||
              s.student_period_fee_paid === false ? (
                <span
                  style={
                    s.enrollment_has_unpaid_nonperiodic_overdue_eps ||
                    s.student_period_fee_has_unpaid_nonperiodic_overdue_eps
                      ? "color: red"
                      : ""
                  }
                >
                  {" (" + "€" + ")"}
                </span>
              ) : (
                ""
              )}
              {wrapInParenthesesIfNotEmpty(
                renderEnrollmentStatus(status, {
                  startedlessonson: s.enrollment_startedlessonson,
                  stoppedlessonson: s.enrollment_stoppedlessonson,
                  lessons: { tag: "dont_show_last_lesson" },
                }),
              ).toLowerCase()}
            </a>
          </div>
        </div>
      );
    });

  const enrollmentrequestsSection = l.enrollmentrequests
    .orderBy(renderFirstNameLastName)
    .map((s) => (
      <div className={style.text_with_icon}>
        <i className={FA.icon_user_solid}></i>
        <div>
          <a
            href={routes.enrollment_requestRoute.link({
              enrollmentrequestid: s.enrollmentrequestid,
            })}
          >
            {renderFirstNameLastName(s)} (
            {
              renderEnrollmentrequestStatus(
                parseEnrollmentRequestStatus(s.status),
              )[0]
            }
            )
          </a>
        </div>
      </div>
    ));

  const addStudentsSection =
    formulaType.tag === "Group" || formulaType.tag === "Camp" ? (
      dyn(addingStudentS, function (addingStudent) {
        if (addingStudent === false) {
          return (
            <div style="text-align: right">
              <button
                className={style.button_small_blue}
                onclick={() => addingStudentS.set(true)}
              >
                <i className={FA.icon_plus_solid}></i>
                {"Voeg student toe"} (
                {"max %maxnumberofstudents".replace(
                  "%maxnumberofstudents",
                  (formulaType.tag === "Group"
                    ? formulaType.maxnumberofstudents
                    : formulaType.maxnumberofstudentscamp
                  ).toString(),
                )}
                )
              </button>
            </div>
          );
        } else {
          return renderAsync(
            Promise.all([
              rpc(routes.getActiveLessonsForLessongroupSpec, undefined, {
                lessongroupid: l.lessongroup.id,
              }),
            ]),
            ([lessons]) => {
              const formula = allFormulas.find((f) => f.id === l.formula.id)!;
              const form = mkEnrollmentFormOnPlanning(
                {
                  tag: "group",
                  lessongroupid: l.lessongroup.id,
                  trial_lesson_payment_scheme_id:
                    formula.trial_lesson_payment_scheme_id,
                  allow_trial_lessons: formula.allow_trial_lessons,
                },
                formula.payment_schemes,
                country,
                schoolurl,
                l.lessongroup.startsinperiod,
                {
                  firstlesson:
                    l.lessongroup.firstLesson || joda.LocalDate.now(),
                  lastlesson: l.lessongroup.lastLesson || joda.LocalDate.now(),
                  period_firstday: period.firstday,
                  numberOfLessons: l.lessongroup.numberoflessons,
                  startedlessonson: date,
                  lessons: lessons.map((l) => l.date),
                  created: joda.LocalDate.now(),
                },
                null,
                each_period_fee_due_date,
                each_period_fee_pay_during_online,
                {
                  max_one_optional_line: formula.max_one_optional_line,
                  periodfee_required: formula.periodfee_required,
                },
              );
              return (
                <div
                  className={style.panel + style.slide_in}
                  style="margin-top: 24px"
                >
                  {form.render()}
                  <div style="text-align: right">
                    <button
                      className={style.button_em_down}
                      onclick={() => addingStudentS.set(false)}
                    >
                      {"Annuleer"}
                    </button>
                    {renderSaveButton({
                      formS: form.s,
                      text: {
                        normal: "Sla op",
                        success: "Opgeslagen!",
                      },
                      timeout: 1500,
                      reload: reload,
                      save: async (f) => {
                        await rpc(routes.saveNewEnrollmentSpec, undefined, {
                          lessongroupid: l.lessongroup.id,
                          enr: {
                            student: f.student,
                            startslessonson: date,
                            invoice: f.invoice_and_expected_payments.invoice,
                            payment_scheme:
                              f.invoice_and_expected_payments.payment_scheme,
                            expected_payments:
                              f.invoice_and_expected_payments.expected_payments,
                            periodfee:
                              f.invoice_and_expected_payments.periodfee,
                            trial_lesson: f.trial_lesson,
                          },
                        });
                        return null;
                      },
                    })}
                  </div>
                </div>
              );
            },
          );
        }
      })
    ) : (
      <span></span>
    );

  const linksSection = (
    <section
      style={
        "margin-bottom: 16px; display: flex; align-items: flex-start; justify-content: space-between"
      }
    >
      <a
        href={routes.lessongroup_generalRoute.link({
          lessongroupid: l.lessongroup.id,
        })}
      >
        <i className={FA.icon_link_solid}></i>
        {"Details lessenreeks"}
      </a>
      <a
        href={routes.lessongroup_lessondataRoute.link({
          lessongroupid: l.lessongroup.id,
          selecteddate: date,
          monthsinfuture: undefined,
        })}
      >
        {"Lesdata"}
        <i className={FA.icon_calendar_alt_regular + style.icon_on_right}></i>
      </a>
    </section>
  );

  const startEditButton = (
    <button
      className={style.button_small_blue + style.text_with_icon}
      style="width: 100%"
      onclick={() => onStartEdit(selectedSlot)}
    >
      <i className={FA.icon_pencil_alt_solid}></i>
      <div>{"Pas aan"}</div>
    </button>
  );

  const makeComplementButton =
    l.lessongroup.planningMode === "Biweekly" &&
    l.complement === "none_planned_yet" ? (
      <div style="margin-top: 16px">
        <button
          className={
            style.button_small + style.button_accent + style.text_with_icon
          }
          style="width: 100%"
          onclick={onStartMakingComplement}
        >
          <i className={FA.icon_plus_solid}></i>
          <div>{"Plan les op andere weken"}</div>
        </button>
      </div>
    ) : (
      <span></span>
    );

  return (
    <div>
      {header}

      <section>
        {instrumentAndLocationSection}

        {lessongroupCommentsSection}

        {lastLessonSection}

        <h1>{"Studenten"}</h1>
        <article>
          {studentsSection}
          {enrollmentrequestsSection}
        </article>

        {addStudentsSection}
      </section>

      {linksSection}

      {startEditButton}

      {makeComplementButton}
    </div>
  );
}

export function renderExistingLessonHeader(
  l: planning_slot_contents_lesson,
  goToNormal: () => void,
) {
  const colors = formulaColorToPlanningColors(l.formula.color);

  return (
    <div
      className={style.panel_header}
      style={`background: ${makeGradient(colors)}; color: ${colors.text}`}
    >
      {renderClosePopupButton(goToNormal)}
      <article className={style.text_with_icon}>
        <i className={FA.icon_glasses}></i>
        <div>
          <b>{renderFirstNameLastName(l.teacheractual)}</b>
          {l.teacherplanned.id !== l.teacheractual.id ? (
            <span>
              {" "}
              |{" "}
              <b>
                {"Vervanging van " +
                  renderFirstNameLastName(l.teacherplanned)}
              </b>
            </span>
          ) : (
            <span></span>
          )}
        </div>
      </article>

      <div style="display: flex">
        <div style="flex: 1">{l.formula.description}</div>
        <div>
          {translateWeekday(l.lesson.date.dayOfWeek()).slice(0, 2) +
            " " +
            renderTimeRange(l.lesson.time, l.lessongroup.lessonduration)}
        </div>
      </div>
    </div>
  );
}

function renderExistingSlot_enrollmentrequest(
  date: joda.LocalDate,
  _selectedSlot: planning_slot,
  l: planning_slot_contents_enrollmentrequest,
  goToNormal: () => void,
) {
  const colors = greyPlanningColors;
  const status = parseEnrollmentRequestStatus(l.enrollmentrequest.status);
  const statusText =
    status === "Reserved" && l.enrollmentrequest.reserveduntil !== null
      ? "Reservatie tot %d".replace(
          "%d",
          l.enrollmentrequest.reserveduntil.format(ddMMyyyy),
        )
      : status === "InvitationER"
      ? "Uitnodiging"
      : "Aanvraag";

  return (
    <div>
      <div
        className={style.panel_header}
        style={`background: ${makeGradient(colors)}; colors: ${colors.text}`}
      >
        {renderClosePopupButton(goToNormal)}
        <article className={style.text_with_icon}>
          <i className={FA.icon_glasses}></i>
          <div>
            <b>{renderFirstNameLastName(l.teacher)}</b> ({statusText})
          </div>
        </article>

        <div style="display: flex">
          <div style="flex: 1">{l.formula.description}</div>
          <div>
            {translateWeekday(date.dayOfWeek()).slice(0, 2) +
              " " +
              renderTimeRange(
                l.enrollmentrequest.time,
                l.formula.lessonduration,
              )}
          </div>
        </div>
      </div>

      <section>
        {/* instr */}
        <article style="display: flex">
          <div style="flex: 1" className={style.text_with_icon}>
            <i className={FA.icon_music_solid}></i>
            <div>{l.instrument.description}</div>
          </div>
          <div style="text-align: right">
            <a
              href={routes.enrollment_requestRoute.link({
                enrollmentrequestid: l.enrollmentrequest.id,
              })}
            >
              {"Details"}
              <i className={FA.icon_arrow_right + style.icon_on_right}></i>
            </a>
          </div>
        </article>

        <h1>{"Studenten"}</h1>
        <a
          href={routes.studentOverviewRoute.link({
            studentid: l.student.id,
          })}
        >
          {renderFirstNameLastName(l.student)}
        </a>
      </section>
    </div>
  );
}

function renderExistingSlot_teacherabsence(
  date: joda.LocalDate,
  selectedSlot: planning_slot,
  l: planning_slot_teacher_absence,
  afterDelete: () => void,
  goToNormal: () => void,
) {
  const removingS = new Source(false);
  const actions = rpc(
    routes.calculateCancelTeacherabsenceActionsSpec,
    undefined,
    {
      internalteacherid: l.teacher.internalteacherid,
      time: selectedSlot.time,
      duration: selectedSlot.duration,
      date: date,
    },
  );
  const removingSection = dyn(removingS, (removing) => {
    if (!removing) {
      return <span></span>;
    }
    return (
      <div className={style.panel + style.slide_in}>
        <article>
          {"Ben je zeker dat je deze afwezigheid wilt verwijderen? Alle bovenstaande wijzigingen zullen worden teruggedraaid."}
        </article>

        <div style="text-align: right">
          <button
            className={style.button_em_down}
            onclick={() => removingS.set(false)}
          >
            {"Annuleer"}
          </button>
          {renderSaveButton({
            save: () =>
              rpc(routes.cancelTeacherAbsenceSpec, undefined, {
                internalteacherid: l.teacher.internalteacherid,
                time: selectedSlot.time,
                duration: selectedSlot.duration,
                date: date,
              }),
            text: {
              normal: "Ja, annuleer afwezigheid",
              success: "Gelukt!",
            },
            reload: afterDelete,
            timeout: 1000,
          })}
        </div>
      </div>
    );
  });

  return (
    <div>
      <div
        className={style.panel_header}
        style={`background: ${makeGradient(greyPlanningColors)}; color: ${
          greyPlanningColors.text
        }; `}
      >
        {renderClosePopupButton(goToNormal)}
        <article className={style.text_with_icon}>
          <i className={FA.icon_glasses}></i>
          <div>
            <b>{renderFirstNameLastName(l.teacher)}</b>
          </div>
        </article>
        <div style="display: flex">
          <div style="flex: 1">{"Afwezig"}</div>
          <div>{renderTimeRange(selectedSlot.time, selectedSlot.duration)}</div>
        </div>
      </div>
      {renderAsync(actions, (actions) => {
        const moved =
          actions.lessonsMoved.length === 0 ? (
            <span></span>
          ) : (
            <article>
              <label>{"Lessen verplaatst:"}</label>
              <ul>
                {actions.lessonsMoved.map((moved) => (
                  <li className={style.text_with_icon}>
                    <i className={FA.icon_user_solid}></i>
                    <div>
                      {"%students -> %date"
                        .replace(
                          "%students",
                          (moved.students || [])
                            .map(renderFirstNameLastName)
                            .join(", "),
                        )
                        .replace(
                          "%date",
                          moved.replacementlessondate?.format(ddMM) || "",
                        )}
                    </div>
                  </li>
                ))}
              </ul>
            </article>
          );
        const deleted =
          actions.lessonsDeleted.length === 0 ? (
            <span></span>
          ) : (
            <article>
              <label>{"Lessen verwijderd:"}</label>
              <ul>
                {actions.lessonsDeleted.map((deleted) => (
                  <li className={style.text_with_icon}>
                    <i className={FA.icon_user_solid}></i>
                    <div>
                      {(deleted.students || [])
                        .map(renderFirstNameLastName)
                        .join(", ")}
                    </div>
                  </li>
                ))}
              </ul>
            </article>
          );
        const withReplacement =
          actions.lessonsWithReplacementTeacher.length === 0 ? (
            <span></span>
          ) : (
            <article>
              <label>{"Lessen aan vervanger toegewezen:"}</label>
              <ul>
                {actions.lessonsWithReplacementTeacher.map(
                  (withReplacement) => (
                    <li className={style.text_with_icon}>
                      <i className={FA.icon_user_solid}></i>
                      <div>
                        {"%students -> %replacement"
                          .replace(
                            "%students",
                            (withReplacement.students || [])
                              .map(renderFirstNameLastName)
                              .join(", "),
                          )
                          .replace(
                            "%replacement",
                            renderFirstNameLastName(
                              withReplacement.replacement,
                            ),
                          )}
                      </div>
                    </li>
                  ),
                )}
              </ul>
            </article>
          );
        return (
          <div>
            {moved}
            {deleted}
            {withReplacement}
          </div>
        );
      })}
      <article style="text-align: right">
        <button onclick={() => removingS.set(true)}>
          {"Annuleer afwezigheid"}
        </button>
      </article>
      {removingSection}
    </div>
  );
}

function renderExistingSlot_teachingperiod(
  _date: joda.LocalDate,
  selectedSlot: planning_slot,
  l: planning_slot_teaching_period,
  afterDelete: () => void,
  goToNormal: () => void,
) {
  return (
    <div>
      <div
        className={style.panel_header}
        style={`background: ${makeGradient(paleBlue)}; color: ${
          paleBlue.text
        }; `}
      >
        {renderClosePopupButton(goToNormal)}
        <article className={style.text_with_icon}>
          <i className={FA.icon_glasses}></i>
          <div>
            <b>{renderFirstNameLastName(l.teacher)}</b>
          </div>
        </article>
        <div style="display: flex">
          <div style="flex: 1">{"Vrij"}</div>
          <div>{renderTimeRange(selectedSlot.time, selectedSlot.duration)}</div>
        </div>
      </div>

      <article>
        <div className={style.text_with_icon}>
          <i className={FA.icon_map_marker_alt_solid}></i>
          <div>{renderLessonLocation(l.location, l.room.description)}</div>
        </div>
      </article>

      {isNil(l.firstday) ? (
        <span></span>
      ) : (
        <article>
          <div className={style.text_with_icon}>
            <i className={FA.icon_calendar_alt_regular}></i>
            <div>
              {"Vanaf %d".replace("%d", ddMMMMyyyy(l.firstday))}
            </div>
          </div>
        </article>
      )}

      <div style="text-align: right">
        {renderSaveButton({
          save: () =>
            rpc(routes.deleteTeachingPeriodSpec, undefined, {
              time: selectedSlot.time,
              weekday: selectedSlot.weekday,
              internalteacherid: selectedSlot.internalteacherid,
              duration: selectedSlot.duration,
            }),
          text: {
            normal: "Verwijder lesperiode",
            success: "Gelukt!",
          },
          reload: afterDelete,
          timeout: 1000,
        })}
      </div>
    </div>
  );
}

function renderNew_private(
  lessongroupFormArgs: lessongroupFormArgs,
  cancel: () => void,
) {
  let lastParsedStudentMem:
    | null
    | {
        tag: "new";
        firstname: string;
        lastname: string;
        email: string;
        phone: string;
        birthday: joda.LocalDate | null;
      }
    | {
        tag: "existing";
        student: {
          id: studentid;
          firstname: string;
          lastname: string;
          birthday: joda.LocalDate | null;
        };
      } = null;
  const fullForm = new Form({})
    .addField("lessongroup", [], async () => {
      return mkLessongroupForm({
        ...lessongroupFormArgs,
        formulas: lessongroupFormArgs.formulas.filter(
          (f) => f.type_.tag === "Private",
        ),
      });
    })
    .addField("student", ["lessongroup"], async ({ lessongroup }) => {
      const orderedLessons = lessongroup.lessondates.lessons.orderBy((d) =>
        d.toEpochDay(),
      );
      return mkEnrollmentFormOnPlanning(
        {
          tag: "private",
          ils: lessongroup.formulaValues.invoicelines.map((il) =>
            adaptInvoiceLineToLg(il),
          ),
        },
        lessongroup.formula.payment_schemes,
        lessongroupFormArgs.country,
        lessongroupFormArgs.schoolurl,
        lessongroupFormArgs.period.id,
        {
          firstlesson: lessongroupFormArgs.firstLessonDate,
          lastlesson: orderedLessons.last()!,
          lessons: orderedLessons,
          period_firstday: lessongroupFormArgs.period.firstday,
          numberOfLessons: lessongroup.lessondates.lessons.length,
          created: joda.LocalDate.now(),
          startedlessonson: orderedLessons.first()!,
        },
        lastParsedStudentMem,
        lessongroupFormArgs.each_period_fee_due_date,
        lessongroupFormArgs.each_period_fee_pay_during_online,
        {
          max_one_optional_line: lessongroup.formula.max_one_optional_line,
          periodfee_required: lessongroup.formula.periodfee_required,
        },
      );
    })
    .build();
  scheduleForCleanup(
    fullForm.s.observe((f) => {
      if (f.tag === "parsed") {
        lastParsedStudentMem = f.parsed.student.student;
      }
    }),
  );
  const saveButton = renderSaveButton({
    save: async (form) => {
      const res = await rpc(routes.saveNewPrivateLessongroupSpec, undefined, {
        student: {
          student: form.student.student,
          invoice: form.student.invoice_and_expected_payments.invoice,
          payment_scheme:
            form.student.invoice_and_expected_payments.payment_scheme,
          expected_payments:
            form.student.invoice_and_expected_payments.expected_payments,
          periodfee: form.student.invoice_and_expected_payments.periodfee,
          startslessonson: lessongroupFormArgs.firstLessonDate,
          trial_lesson: false,
        },
        lessongroup: {
          ...form.lessongroup,
          formulaValues: {
            ...form.lessongroup.formulaValues,
            invoicelines: form.lessongroup.formulaValues.invoicelines.map(
              (il) => adaptInvoiceLineToLg(il),
            ),
          },
        },
      });
      if (isNumber(res)) {
        return { tag: "success" };
      } else {
        return res;
      }
    },
    text: {
      normal: "Sla op",
      success: "Gelukt!",
    },
    reload: (f) =>
      lessongroupFormArgs.onSuccessfulSave({
        time: f.lessongroup.time,
        weekday: lessongroupFormArgs.weekday,
        internalteacherid: lessongroupFormArgs.teacher.internalteacherid,
      }),
    formS: fullForm.s,
    timeout: 1000,
  });

  return (
    <div>
      {fullForm.render()}
      <div style="text-align: right">
        <button className={style.button_em_down} onclick={cancel}>
          {"Annuleer"}
        </button>
        {saveButton}
      </div>
    </div>
  );
}

function renderNew_group_or_camp(
  lessongroupFormArgs: lessongroupFormArgs,
  cancel: () => void,
  subtype: "Group" | "Camp",
) {
  const lgForm = mkLessongroupForm({
    ...lessongroupFormArgs,
    formulas: lessongroupFormArgs.formulas.filter(
      (f) => f.type_.tag === subtype,
    ),
  });

  const saveButton = renderSaveButton({
    save: async (form) => {
      const res = await rpc(routes.saveNewGrouplessonSpec, undefined, {
        ...form,
        formulaValues: {
          ...form.formulaValues,
          invoicelines: form.formulaValues.invoicelines.map((il) =>
            adaptInvoiceLineToLg(il),
          ),
        },
      });
      if ("tag" in res) {
        return res;
      } else {
        return null;
      }
    },
    text: {
      normal: "Sla op",
      success: "Gelukt!",
    },
    reload: (f) =>
      lessongroupFormArgs.onSuccessfulSave({
        time: f.time,
        weekday: lessongroupFormArgs.weekday,
        internalteacherid: lessongroupFormArgs.teacher.internalteacherid,
      }),
    formS: lgForm.s,
    timeout: 1000,
  });
  return (
    <div>
      {lgForm.render()}
      <div style="text-align: right">
        <button className={style.button_em_down} onclick={cancel}>
          {"Annuleer"}
        </button>
        {saveButton}
      </div>
    </div>
  );
}

function renderNew_teachingperiod(
  weekday: joda.DayOfWeek,
  internalteacherid: internalteacherid,
  sources: { timeS: Source<joda.LocalTime>; durationS: Source<number> },
  reload: (sl: {
    weekday: joda.DayOfWeek;
    time: joda.LocalTime;
    internalteacherid: internalteacherid;
  }) => void,
  cancel: () => void,
  dateSelected: joda.LocalDate,
) {
  const untilS = new Source(
    sources.timeS.get().plusMinutes(sources.durationS.get()),
  );
  scheduleForCleanup(
    untilS.observe((until) => {
      sources.durationS.set(
        joda.Duration.between(sources.timeS.get(), until).toMinutes(),
      );
    }),
  );
  const form = new Form({})
    .addField("from", [], () =>
      timeBox({
        initialVal: sources.timeS.get(),
        label: "Van",
        previewS: sources.timeS,
      }),
    )
    .addField("until", [], () =>
      timeBox({
        initialVal: untilS.get(),
        previewS: untilS,
        label: "Tot",
      }),
    )
    .addField("duration", ["from", "until"], ({ from, until }) =>
      constantField(joda.Duration.between(from, until).toMinutes()),
    )
    .addField("location", ["from", "duration"], async ({ from, duration }) => {
      const locs = await rpc(
        routes.getAvailableRoomsForTeachingPeriodSpec,
        undefined,
        {
          internalteacherid: internalteacherid,
          wd: weekday,
          from,
          duration,
        },
      );
      return selectBox({
        values: locs,
        initial: locs[0],
        show: (l) => l.description,
        label: "Lokaal",
      });
    })
    .addField("firstday", ["from", "until"], async () =>
      radioField({
        initial: "none" as const,
        values: ["none" as const, "selected_date" as const],
        show: (t) =>
          t === "none"
            ? "Altijd"
            : "Vanaf %d".replace("%d", dateSelected.format(ddMMyyyy)),
        render: radioFieldCheckboxRenderInputLike,
        containerClass: style.equal_columns_non_responsive,
        label: "Actief",
      }),
    )
    .build({
      render: (fields) => (
        <div>
          <div className={style.equal_columns_non_responsive}>
            {fields.from()}
            {fields.until()}
          </div>
          {fields.location()}
          {fields.firstday()}
        </div>
      ),
    });

  return (
    <div>
      <div className={style.panel_header + planningstyle.slot_new}>
        {renderClosePopupButton(cancel)}
        {"Nieuwe vrije lesperiode"}
      </div>
      <section>{form.render()}</section>
      <div style="text-align: right">
        <button className={style.button_em_down} onclick={cancel}>
          {"Annuleer"}
        </button>
        {renderSaveButton({
          formS: form.s,
          save: async (form) => {
            const newTime = await rpc(
              routes.saveTeachingperiodSpec,
              undefined,
              {
                internalteacherid,
                weekday,
                firstday:
                  form.firstday === "none"
                    ? null
                    : form.firstday === "selected_date"
                    ? dateSelected
                    : checkAllCasesHandled(form.firstday),
                from: form.from,
                duration: form.duration,
                location: form.location,
              },
            );
            setTimeout(
              () => reload({ weekday, time: newTime, internalteacherid }),
              1000,
            );
          },
          reload: false,
          timeout: 1000,
        })}
      </div>
    </div>
  );
}

function renderNew_absence(
  date: joda.LocalDate,
  internalteacherid: internalteacherid,
  sources: { timeS: Source<joda.LocalTime>; durationS: Source<number> },
  slots: planning_slot[],
  reload: (sl: {
    weekday: joda.DayOfWeek;
    time: joda.LocalTime;
    internalteacherid: internalteacherid;
  }) => void,
  cancel: () => void,
) {
  const form = renderTeacherAbsenceForm(
    date,
    internalteacherid,
    sources,
    slots,
  );
  return (
    <div>
      <div className={style.panel_header + planningstyle.slot_new}>
        {renderClosePopupButton(cancel)}
        {"Docent afwezigheid"}
      </div>
      <section>{form.render()}</section>

      <div style="text-align: right">
        <button className={style.button_em_down} onclick={cancel}>
          {"Annuleer"}
        </button>
        {renderSaveButton({
          formS: form.s,
          save: (f) => rpc(routes.saveTeacherAbsenceSpec, undefined, f),
          reload: (f) =>
            reload({
              time: f.time,
              internalteacherid,
              weekday: date.dayOfWeek(),
            }),
          timeout: 1000,
        })}
      </div>
    </div>
  );
}

function renderLessonEdit(
  l: planning_slot_contents_lesson,
  opts: editLessongroupFormOptions,
  handlers: {
    onStartMoving: (mode: "lesson" | "lessongroup") => void;
    onStartMovingEnrollment: (student: {
      enrollmentid: enrollmentid;
      firstname: string;
      lastname: string;
      id: studentid;
    }) => void;
  },
  goToNormal: () => void,
): HTMLElement | HTMLElement[] {
  const header = renderExistingLessonHeader(l, goToNormal);

  const form = renderEditLessonForm(l, opts);

  const enrollmentsP = rpc(
    routes.getAllEnrollmentsForLessongroupSpec,
    undefined,
    {
      lessongroupid: l.lessongroup.id,
    },
  );

  const studentsSection = renderAsync(enrollmentsP, (enrs) =>
    enrs
      .orderBy([(s) => s.status, renderFirstNameLastName], ["asc", "asc"])
      .map((s) => {
        const deletingEnrollmentS = new Source(false);
        return (
          <div>
            <div>
              <article style="display: flex;">
                <div style="flex: 1;" className={style.text_with_icon}>
                  <i className={FA.icon_user_solid}></i>
                  <div>
                    {renderFirstNameLastName(s)}{" "}
                    {s.status === "Stopped"
                      ? wrapInParentheses("Stopgezet")
                      : s.status === "TrialLesson"
                      ? wrapInParentheses("Proefles")
                      : s.status === "ActiveEnrollment"
                      ? ""
                      : checkAllCasesHandled(s.status)}
                  </div>
                </div>

                <div
                  style={
                    parseFormulaType(l.lessongroup.type).tag === "Private"
                      ? "display: none;"
                      : ""
                  }
                >
                  <button
                    style={
                      s.status === "ActiveEnrollment" ? "" : "display: none"
                    }
                    className={style.button_small}
                    onclick={() => handlers.onStartMovingEnrollment(s)}
                  >
                    <i className={FA.icon_calendar_alt_regular}></i>
                    {"Verplaats student"}
                  </button>

                  <button
                    className={style.button_small + style.button_has_only_icon}
                    onclick={() => {
                      deletingEnrollmentS.set(true);
                    }}
                  >
                    <i className={FA.icon_trash_alt_solid}></i>
                  </button>
                </div>
              </article>
            </div>
            {dyn(deletingEnrollmentS, (deleting) => {
              if (deleting === false) {
                return <span></span>;
              } else {
                const form = makeStopForm(s);
                return (
                  <div className={style.panel + style.slide_in}>
                    <article>{form.render()}</article>
                    <div style="text-align: right">
                      <button
                        className={style.button_em_down}
                        onclick={() => deletingEnrollmentS.set(false)}
                      >
                        {"Annuleer"}
                      </button>
                      {renderSaveButton({
                        formS: form.s,
                        save: (f) =>
                          rpc(routes.stopGroupEnrollmentSpec, undefined, {
                            enrollmentid: s.enrollmentid,
                            fromdate: l.lesson.date,
                            ...f,
                          }),
                        reload: opts.reload,
                        timeout: 1000,
                      })}
                    </div>
                  </div>
                );
              }
            })}
          </div>
        );
      }),
  );

  const deletingLessongroupS = new Source(false);

  return (
    <div>
      {header}

      {opts.teacherMode && opts.teacherMode.onlyEditExistingLgs === true ? (
        []
      ) : (
        <div>
          {form.render()}

          <article style="text-align: right">
            <button className={style.button_em_down} onclick={opts.reload}>
              {"Annuleer"}
            </button>
            {renderSaveButton({
              formS: form.s,
              text: {
                normal: "Sla op",
                success: "Opgeslagen!",
              },
              timeout: 1500,
              reload: opts.reload,
              save: async (f) => {
                return await rpc(routes.changeLessongroupSpec, undefined, {
                  lessongroupId: l.lessongroup.id,
                  formula: {
                    ...f.formula,
                    ...f.formulaValues,
                  },
                  location: {
                    location: f.location.location,
                    roomId: f.location.roomid,
                  },
                  instrumentid: f.instrument.id,
                  datefrom: opts.datefrom,
                });
              },
            })}
          </article>

          <hr />

          <h1>{"Studenten"}</h1>
          {studentsSection}

          <hr />
        </div>
      )}

      {l.lessongroup.planningMode === "Weekly" ||
      l.lessongroup.planningMode === "Biweekly" ? (
        <article>
          <button
            style="width: 100%"
            className={style.text_with_icon}
            onclick={() => handlers.onStartMoving("lessongroup")}
          >
            <i className={FA.icon_arrow_right}></i>
            <div>{"Verplaats volledige lessenreeks"}</div>
          </button>
        </article>
      ) : (
        <span></span>
      )}
      <article>
        <button
          style="width: 100%"
          className={style.text_with_icon}
          onclick={() => handlers.onStartMoving("lesson")}
        >
          <i className={FA.icon_arrow_right}></i>
          <div>{"Verplaats deze les"}</div>
        </button>
      </article>

      {opts.teacherMode && opts.teacherMode.onlyEditExistingLgs === true ? (
        []
      ) : (
        <article>
          <button
            style="width: 100%"
            className={style.button_em_down + style.text_with_icon}
            onclick={() => deletingLessongroupS.set(true)}
          >
            <i className={FA.icon_trash_alt_solid}></i>
            <div>{"Verwijder volledige lessenreeks"}</div>
          </button>
        </article>
      )}

      {dyn(deletingLessongroupS, function (deletingLg) {
        if (deletingLg === false) {
          return <span></span>;
        }
        return renderDeleteLessongroupPanel(
          deletingLessongroupS,
          l,
          enrollmentsP,
          opts,
        );
      })}
    </div>
  );
}

function renderMovePopupContents(
  mode: "lesson" | "lessongroup",
  moving: planning_slot_contents_lesson,
  movingTo: {
    teacher: {
      firstname: string;
      lastname: string;
      internalteacherid: internalteacherid;
    };
    weekday: joda.DayOfWeek;
    initialTime: joda.LocalTime;
  },
  opts: {
    datefrom: joda.LocalDate;
    reload: (sl: {
      internalteacherid: internalteacherid;
      weekday: joda.DayOfWeek;
      time: joda.LocalTime;
    }) => void;
    slots: planning_slot[];
    sources: { timeS: Source<joda.LocalTime> };
  },
): HTMLElement | HTMLElement[] {
  const destinationDate = getFirstWeekdayFrom(
    getMonday(opts.datefrom),
    movingTo.weekday,
  );
  const form = makeMoveForm(
    {
      lessongroupid: moving.lessongroup.id,
      lessonduration: moving.lessongroup.lessonduration,
      instrument: moving.instrument,
      room: {
        description: moving.room.description || "",
        location: moving.lessongroup.location,
        roomId: moving.lessongroup.roomId,
      },
    },
    {
      initialTime: movingTo.initialTime,
      firstDestinationDate: destinationDate,
      teacher: movingTo.teacher,
    },
    opts.sources,
  );

  const cancelButton = (
    <button
      className={style.button_em_down}
      onclick={() =>
        opts.reload({
          internalteacherid: moving.teacheractual.internalteacherid,
          weekday: moving.lesson.date.dayOfWeek(),
          time: moving.lesson.time,
        })
      }
    >
      {"Annuleer"}
    </button>
  );

  const saveButton = renderSaveButton({
    save: async (form) => {
      return rpc(routes.moveLessonOrLessongroupSpec, undefined, {
        mode,
        fromDate: opts.datefrom,
        destinationDate: destinationDate,
        lessonid: moving.lesson.id,
        lessongroupid: moving.lessongroup.id,
        weekday: movingTo.weekday,
        time: form.time,
        internalteacherid: movingTo.teacher.internalteacherid,
        location: form.location,
      });
    },
    text: {
      normal: "Sla op",
      success: "Gelukt!",
    },
    reload: (f) =>
      opts.reload({
        time: f.time,
        weekday: movingTo.weekday,
        internalteacherid: movingTo.teacher.internalteacherid,
      }),
    formS: form.s,
    timeout: 1000,
  });

  const colors = formulaColorToPlanningColors(moving.formula.color);

  const titleText =
    mode === "lesson"
      ? "Verplaats les"
      : mode === "lessongroup"
      ? "Verplaats lessenreeks"
      : checkAllCasesHandled(mode);

  const confirmationText =
    mode === "lesson"
      ? "Ben je zeker dat je deze les: "
      : mode === "lessongroup"
      ? "Ben je zeker dat je deze volledige lessenreeks: "
      : checkAllCasesHandled(mode);

  const fromDateSection = renderSection(
    FA.icon_calendar_alt_regular,
    mode === "lesson"
      ? moving.lesson.date.format(ddMM)
      : mode === "lessongroup"
      ? translateWeekday(parseWeekday(moving.lessongroup.weekday))
      : checkAllCasesHandled(mode),
  );

  const toDateSection = renderSection(
    FA.icon_calendar_alt_regular,
    mode === "lesson"
      ? destinationDate.format(ddMM)
      : mode === "lessongroup"
      ? translateWeekday(movingTo.weekday)
      : checkAllCasesHandled(mode),
  );

  return (
    <div>
      <div
        className={style.panel_header}
        style={`background: ${colors.primary}; color: ${colors.text}`}
      >
        {titleText}
      </div>
      <article>
        <div>{confirmationText}</div>
        <ul style="margin-left: 16px; margin-bottom: 16px">
          {renderSection(
            FA.icon_glasses,
            renderFirstNameLastName(moving.teacherplanned),
          )}

          {fromDateSection}

          {renderSection(
            FA.icon_clock_regular,
            moving.lessongroup.time.format(HHmm),
          )}
        </ul>

        <div>{"Wilt verplaatsen naar:"}</div>
        <ul style="margin-left: 16px; margin-bottom: 16px">
          {renderSection(
            FA.icon_glasses,
            renderFirstNameLastName(movingTo.teacher),
          )}

          {toDateSection}
        </ul>
      </article>

      <article>{form.render()}</article>

      <div style="text-align: right">
        {cancelButton}
        {saveButton}
      </div>
    </div>
  );
}

function renderSection(icon: string, label: string) {
  return (
    <li className={style.text_with_icon}>
      <i className={icon}></i>
      <div>{label}</div>
    </li>
  );
}

function renderMovingEnrollment(
  movingFrom: planning_slot_contents_lesson,
  student: { firstname: string; lastname: string; enrollmentid: enrollmentid },
  movingTo: planning_slot_contents_lesson,
  reload: (sl: {
    internalteacherid: internalteacherid;
    weekday: joda.DayOfWeek;
    time: joda.LocalTime;
  }) => void,
): HTMLElement | HTMLElement[] {
  const colors = formulaColorToPlanningColors(movingTo.formula.color);

  const cancelButton = (
    <button
      className={style.button_em_down}
      onclick={() =>
        reload({
          internalteacherid: movingFrom.teacheractual.internalteacherid,
          weekday: movingFrom.lesson.date.dayOfWeek(),
          time: movingFrom.lesson.time,
        })
      }
    >
      {"Annuleer"}
    </button>
  );

  const saveButton = renderSaveButton({
    save: async () => {
      return rpc(routes.moveEnrollmentSpec, undefined, {
        enrollmentid: student.enrollmentid,
        newLessongroup: movingTo.lessongroup.id,
      });
    },
    text: {
      normal: "Verplaats inschrijving",
      success: "Gelukt!",
    },
    reload: () =>
      reload({
        time: movingTo.lesson.time,
        weekday: movingTo.lesson.date.dayOfWeek(),
        internalteacherid: movingTo.teacheractual.internalteacherid,
      }),
    timeout: 1000,
  });

  return (
    <div>
      <div
        className={style.panel_header}
        style={`background: ${colors.primary}; color: ${colors.text}`}
      >
        {"Verplaats inschrijving"}
      </div>
      <article>
        <div>{"Ben je zeker dat je de inschrijving van"}</div>
        <ul style="margin-left: 16px; margin-bottom: 16px">
          {renderSection(FA.icon_user_solid, renderFirstNameLastName(student))}
        </ul>

        <div>{"Wilt verplaatsen naar de lessenreeks:"}</div>
        <ul style="margin-left: 16px; margin-bottom: 16px">
          {renderSection(
            FA.icon_glasses,
            renderFirstNameLastName(movingTo.teacherplanned),
          )}

          {renderSection(
            FA.icon_calendar_alt_regular,
            translateWeekday(parseWeekday(movingTo.lessongroup.weekday)),
          )}

          {renderSection(
            FA.icon_clock_regular,
            movingTo.lessongroup.time.format(HHmm),
          )}
        </ul>
      </article>
      <article>
        {"De factuur van de nieuwe formule zal toegepast worden."}
      </article>

      <div style="text-align: right">
        {cancelButton}
        {saveButton}
      </div>
    </div>
  );
}

export function renderClosePopupButton(onclick: () => void) {
  return (
    <button className={planningstyle.close_popup_button} onclick={onclick}>
      <i className={FA.icon_plus_solid} style="transform: rotate(45deg);"></i>
    </button>
  );
}

export function maybeRenderFlexCount(
  lg: {
    id: lessongroupid;
    numberoflessons: number;
    planningMode: planningmode;
  },
  formula: { id: formulaid; numberoflessons: number },
): HTMLElement {
  return match(lg.planningMode)
    .with("Flex", () => {
      const text = `beurten: %planned / %total`
        .replace("%planned", lg.numberoflessons.toString())
        .replace("%total", formula.numberoflessons.toString());
      return (
        <span style="display: inline-block; margin-left: 8px;">
          (
          <a
            href={routes.lessongroup_lessondataRoute.link({
              lessongroupid: lg.id,
              monthsinfuture: undefined,
              selecteddate: undefined,
            })}
          >
            {text}
          </a>
          )
        </span>
      );
    })
    .with("Weekly", "Biweekly", "Manual", () => <span></span>)
    .exhaustive();
}
